import { ShoppingBag, FileText, Archive, BarChart3, Settings, Truck, Inbox, PackageSearch, LineChart, Wrench } from "lucide-react";
import { cn } from "@/lib/utils";
import { useCompanyInfo } from "@/hooks/useCompanyInfo";
import { Sheet, SheetContent } from "@/components/ui/sheet";

const LOGO = "/dr-rumble-tumble.png";

const allMenuItems = [
  { id: "dashboard", label: "Dashboard", icon: BarChart3, perm: "dashboard.view" },
  { id: "analytics", label: "Analytics", icon: LineChart, perm: "analytics.view", adminOnlyByDefault: true },
  { id: "orders", label: "Orders", icon: ShoppingBag, perm: "orders.view" },
  { id: "picking", label: "Dispatch", icon: Truck, perm: "dispatch.manage" },
  { id: "receiving", label: "Receiving", icon: Inbox, perm: "receiving.manage" },
  { id: "inventory", label: "Inventory", icon: PackageSearch, perm: "inventory.manage" },
  { id: "maintenance", label: "Maintenance", icon: Wrench, perm: "inventory.manage" },
  { id: "audit", label: "Audit Logs", icon: FileText, perm: "audit.view", adminOnlyByDefault: true },
  { id: "archived", label: "Archived", icon: Archive, perm: "archived.view" },
  // Settings groups multiple admin features (users, products, routes, company, announcements).
  // It is shown if the user has ANY of those manage perms — granular gating
  // happens inside the Settings page itself.
  { id: "settings", label: "Settings", icon: Settings, perm: [
    "users.manage_hotels", "users.manage_staff",
    "products.manage", "routes.manage", "company.manage", "announcements.manage",
  ] },
];

function hasPerm(userRole, userPerms, perm) {
  if (userRole === "admin") return true;
  if (userRole !== "manager" && userRole !== "customer") return false;
  if (userRole === "customer") return false;
  // Staff: check explicit permissions array
  if (Array.isArray(perm)) return perm.some((p) => userPerms.includes(p));
  return userPerms.includes(perm);
}

function SidebarContent({ activeTab, onTabChange, menuItems, brandName, brandTagline }) {
  return (
    <div className="flex flex-col h-full bg-white border-r border-slate-200/80">
      <div className="px-5 h-16 flex items-center border-b border-slate-200/80">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-50 to-sky-100 flex items-center justify-center shadow-sm ring-1 ring-sky-200/60 shrink-0">
            <img src={LOGO} alt={brandName} className="w-8 h-8 object-contain" />
          </div>
          <div className="min-w-0">
            <h2 className="font-display font-bold text-[15px] text-slate-900 tracking-tight truncate leading-tight">
              {brandName}
            </h2>
            <p className="text-[9px] uppercase tracking-[0.18em] text-sky-600 font-mono font-semibold mt-0.5 truncate">
              {brandTagline}
            </p>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
        <p className="px-3.5 text-[10px] uppercase tracking-[0.18em] text-slate-400 font-mono font-bold mb-3">
          Menu
        </p>
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          return (
            <button
              key={item.id}
              onClick={() => onTabChange(item.id)}
              data-testid={`${item.id}-tab`}
              className={cn(
                "w-full group flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm transition-all duration-150 min-h-[44px]",
                isActive
                  ? "font-semibold text-white bg-gradient-to-r from-[#0F3B5C] to-[#155A8A] shadow-md shadow-sky-900/20"
                  : "font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              )}
            >
              <Icon
                className={cn(
                  "w-[18px] h-[18px] transition-colors shrink-0",
                  isActive ? "text-sky-200" : "text-slate-400 group-hover:text-sky-600"
                )}
                strokeWidth={isActive ? 2 : 1.8}
              />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      <div className="px-5 py-4 border-t border-slate-200/80">
        <p className="text-[10px] text-slate-400 uppercase tracking-[0.18em] font-mono font-medium">
          v2.0 · Supabase
        </p>
      </div>
    </div>
  );
}

export default function Sidebar({ activeTab, onTabChange, userRole, userPermissions = [], mobileOpen = false, onMobileClose }) {
  const { companyInfo } = useCompanyInfo();
  const menuItems = allMenuItems.filter((item) => hasPerm(userRole, userPermissions, item.perm));

  const brandName = companyInfo?.system_name || "Dr Rumble Tumble";
  const brandTagline = companyInfo?.system_subtitle || "Hotel Linen System";

  const handleMobileTabChange = (id) => {
    onTabChange(id);
    onMobileClose?.();
  };

  return (
    <>
      {/* Desktop sidebar — fixed left rail, hidden on small screens */}
      <div className="hidden lg:flex w-64 h-screen sticky top-0 shrink-0 shadow-sm">
        <SidebarContent
          activeTab={activeTab}
          onTabChange={onTabChange}
          menuItems={menuItems}
          brandName={brandName}
          brandTagline={brandTagline}
        />
      </div>

      {/* Mobile drawer */}
      <Sheet open={mobileOpen} onOpenChange={(o) => !o && onMobileClose?.()}>
        <SheetContent side="left" className="w-72 p-0 lg:hidden">
          <SidebarContent
            activeTab={activeTab}
            onTabChange={handleMobileTabChange}
            menuItems={menuItems}
            brandName={brandName}
            brandTagline={brandTagline}
          />
        </SheetContent>
      </Sheet>
    </>
  );
}
